import { create } from "zustand";

export type ToastType = "success" | "error" | "info" | "warning";

export interface ToastAction {
  label: string;
  primary?: boolean;
  onClick?: () => void;
}

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  dismissable: boolean;
  durationMs: number;
  exiting?: boolean;
  actions?: readonly ToastAction[];
}

export interface ConfirmDialogRequest {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  variant: "danger" | "primary";
}

interface PendingConfirm extends ConfirmDialogRequest {
  resolve: (confirmed: boolean) => void;
}

export interface BreadcrumbEntry {
  label: string;
  to: string;
  status?: string;
}

interface UiState {
  toasts: readonly Toast[];
  confirm: PendingConfirm | null;
  breadcrumb: readonly BreadcrumbEntry[];
  isShortcutHelpOpen: boolean;
  /** Member row to flash after a save on Group Overview. */
  highlightedMemberId: string | null;
  addToast: (message: string, type?: ToastType, durationMs?: number, actions?: readonly ToastAction[], dismissable?: boolean) => string;
  dismissToast: (id: string) => void;
  removeToast: (id: string) => void;
  requestConfirm: (request: ConfirmDialogRequest) => Promise<boolean>;
  resolveConfirm: (confirmed: boolean) => void;
  setBreadcrumb: (entries: readonly BreadcrumbEntry[]) => void;
  setShortcutHelpOpen: (open: boolean) => void;
  setHighlightedMemberId: (memberId: string | null) => void;
}

const EXIT_ANIMATION_MS = 200;
export const DEFAULT_TOAST_MS = 4000;

let toastCounter = 0;

export const useUiStore = create<UiState>()((set, get) => ({
  toasts: [],
  confirm: null,
  breadcrumb: [],
  isShortcutHelpOpen: false,
  highlightedMemberId: null,

  addToast: (message, type = "info", durationMs = DEFAULT_TOAST_MS, actions, dismissable = true) => {
    toastCounter += 1;
    const id = `toast-${toastCounter}`;
    set((state) => ({ toasts: [...state.toasts, { id, message, type, dismissable, durationMs, actions }] }));
    if (durationMs > 0) setTimeout(() => get().dismissToast(id), durationMs);
    return id;
  },

  dismissToast: (id) => {
    set((state) => ({ toasts: state.toasts.map((toast) => (toast.id === id ? { ...toast, exiting: true } : toast)) }));
    setTimeout(() => get().removeToast(id), EXIT_ANIMATION_MS);
  },

  removeToast: (id) => set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) })),

  requestConfirm: (request) =>
    new Promise<boolean>((resolve) => {
      set({ confirm: { ...request, resolve } });
    }),

  resolveConfirm: (confirmed) => {
    const pending = get().confirm;
    set({ confirm: null });
    pending?.resolve(confirmed);
  },

  setBreadcrumb: (entries) => set({ breadcrumb: entries }),
  setShortcutHelpOpen: (open) => set({ isShortcutHelpOpen: open }),
  setHighlightedMemberId: (memberId) => set({ highlightedMemberId: memberId }),
}));

export function notify(message: string, type: ToastType = "info"): void {
  useUiStore.getState().addToast(message, type);
}

export function confirmAction(request: ConfirmDialogRequest): Promise<boolean> {
  return useUiStore.getState().requestConfirm(request);
}
